import * as THREE from "three";
import type { Target } from "../targets";
import { disposeWorld } from "./level-meshes";
import { inked, toon } from "./toon";

// The map's paintball targets (#137): a paint can, a round sign on a post,
// a bullseye on a stand, each sized to its hit volume (`TARGET_KINDS`) and
// facing the way its map file says. A burst one pops (swells and is gone in
// a blink) and stays down while the skater's `PackState.burst` has it.

/** How long a burst target takes to pop (s). */
const POP_TIME = 0.18;

/** A disc of rings, facing +z: red and white, a bullseye in the middle. */
function rings(r: number, n: number): THREE.Group {
  const g = new THREE.Group();
  for (let i = 0; i < n; i++) {
    const rr = r * (1 - i / n);
    const disc = inked(new THREE.CylinderGeometry(rr, rr, 0.04 + 0.004 * i, 28), toon(i % 2 ? "#ffffff" : "#e8333d", { roughness: 0.5 }, "paint"), i ? 0 : 0.012);
    disc.rotation.x = Math.PI / 2;
    disc.position.z = 0.002 * i;
    g.add(disc);
  }
  return g;
}

function post(height: number): THREE.Mesh {
  const geo = new THREE.CylinderGeometry(0.04, 0.05, height, 10);
  geo.translate(0, height / 2, 0);
  return inked(geo, toon("#6b6f78", { roughness: 0.6 }, "metal"), 0.008);
}

function buildTarget(t: Target): THREE.Group {
  const g = new THREE.Group();
  const up = t.y - t.ground;
  if (t.kind === "can") {
    const can = inked(new THREE.CylinderGeometry(t.r * 0.8, t.r * 0.8, up * 2, 18), toon("#2fd0ff", { roughness: 0.4 }, "paintedMetal"), 0.012);
    can.position.y = up;
    const lid = inked(new THREE.CylinderGeometry(t.r * 0.82, t.r * 0.82, 0.03, 18), toon("#c8ccd4", { roughness: 0.3 }, "metal"), 0.008);
    lid.position.y = up * 2;
    g.add(can, lid);
  } else {
    g.add(post(up));
    const face = rings(t.r, t.kind === "bullseye" ? 5 : 3);
    face.position.y = up;
    face.position.z = 0.05;
    g.add(face);
    if (t.kind === "bullseye") {
      // Two legs behind, an easel.
      for (const s of [-1, 1]) {
        const leg = post(up * 1.05);
        leg.position.set(s * 0.25, 0, -0.25);
        leg.rotation.x = 0.25;
        g.add(leg);
      }
    }
  }
  g.position.set(t.x, t.ground, t.z);
  g.rotation.y = t.yaw;
  return g;
}

export class TargetMeshes {
  readonly root = new THREE.Group();
  private items: THREE.Group[];
  /** Seconds into each target's pop, or -1 while standing (or long gone). */
  private pop: number[];

  constructor(targets: readonly Target[]) {
    this.root.name = "targets";
    this.items = targets.map(buildTarget);
    this.pop = targets.map(() => -1);
    this.root.add(...this.items);
  }

  /** Shows the standing ones; one burst since the last frame pops. */
  update(burst: ReadonlySet<number>, dt: number): void {
    this.items.forEach((g, i) => {
      if (!burst.has(i)) {
        g.visible = true;
        g.scale.setScalar(1);
        this.pop[i] = -1;
        return;
      }
      if (this.pop[i]! < 0 && g.visible) this.pop[i] = 0;
      if (this.pop[i]! >= 0) {
        this.pop[i]! += dt;
        const p = this.pop[i]! / POP_TIME;
        g.scale.setScalar(1 + 0.5 * p);
        if (p >= 1) {
          g.visible = false;
          this.pop[i] = -2;
        }
      }
      if (this.pop[i] === -2) g.visible = false;
    });
  }

  dispose(): void {
    disposeWorld(this.root);
  }
}
